<template>
  <div class="av-container">
    <div class="app-details">
      <div class="app-grp">
        <div class="app-name">{{ application.applicationTypeName }}</div>
        <div class="app-detail-info">Application ID: {{ application.referenceId }}</div>
        <div class="app-detail-info">
          Started on {{ formattedDateFormat(application.created, "MM/DD/YYYY") }} | Expires on
          {{ formattedDateFormat(application.expires, "MM/DD/YYYY") }}
        </div>
      </div>
      <div class="app-cancel" v-if="showCancellationLink">
        <a @click="openCancellationModal">Cancel Application</a>
      </div>
    </div>
    <div class="app-status">
      <div>Application Status</div>
      <div class="app-further-details">
        <div class="app-stat-desc">
          <div class="circle">{{ application.applicationStatusId }}</div>
          <div class="app-st">{{ application.applicationStatusName }}</div>
        </div>
        <input
          type="button"
          class="app-view"
          value="View Application"
          @click="viewApplication(application.referenceId)"
        />
      </div>
    </div>
    <cancellation-modal
      v-if="cancelApplication"
      :application="application"
      @cancelApplication="emit('applicationCancelled')"
    />
  </div>
</template>
<script setup lang="ts">
import { defineProps, withDefaults, defineEmits, computed, onMounted } from "vue";
import { Application } from "@/models/application";
import useFormatter from "@/use/formatter";
import router from "../router";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import CancellationModal from "@/components/modals/CancellationModal.vue";
import { ApplicationStatusTypeIds, ProductIds } from "@/config/constants";

interface Props {
  application: Application;
}
const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits(["applicationCancelled"]);
const { formattedDateFormat } = useFormatter();
const route = useRoute();
const store = useStore();
const cancelApplication = computed(() => store.getters["modal/cancellationModalActive"]);
const applicationStatusId = computed(() => props.application.applicationStatusId);

const showCancellationLink = computed(() => {
  return (
    applicationStatusId.value === ApplicationStatusTypeIds.NOT_SUBMITTED ||
    applicationStatusId.value === ApplicationStatusTypeIds.EVALUATION ||
    applicationStatusId.value === ApplicationStatusTypeIds.SUBMITTED_AND_PROCESSING ||
    applicationStatusId.value === ApplicationStatusTypeIds.RIGHT_TO_CANCEL
  );
});

function viewApplication(referenceId: string | undefined) {
  const query = route.query as any;
  // redirect to application page
  router.push({ path: `/applications/${referenceId}`, query });
}

function openCancellationModal() {
  store.dispatch("modal/openCancellationModal", props.application.referenceId);
}

onMounted(async () => {
  await store.dispatch("lookupsModule/getLookups");
});
</script>
<style lang="scss" scoped>
.av-container {
  width: 617px;
  height: 180px;
  background-color: #fff;
  padding: 0 15px;
  position: relative;
  z-index: 2;
}
.app-details {
  color: #000;
  font-family: "Montserrat", arial, sans-serif;
  display: flex;
  line-height: 17.175px;
  margin-top: 12px;
}
.app-grp {
  flex: 1 1 60%;
}
.app-name {
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
}
.app-detail-info {
  font-weight: 400;
  margin-top: 9px;
}
.app-cancel {
  color: #00f;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}
.app-status {
  color: #000;
  font-family: "Montserrat", arial, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 17.175px;
  padding-top: 20px;
}
.app-further-details {
  display: flex;
  flex-direction: row;
  margin-top: 9px;
}
.app-st {
  flex: 1 1 60%;
  font-weight: 700;
}
.app-view {
  display: flex;
  width: 183px;
  height: 34px;
  border-radius: 4px;
  color: #ffffff;
  background: var(--theme-color, #f7941e);
  font-weight: 500;
}
.app-stat-desc {
  display: flex;
  flex: 1 1 60%;
  margin: auto;
}
.circle {
  background: var(--theme-color, #f7941e);
  position: relative;
  width: 1.5em;
  height: 1.5em;
  line-height: 1.5em;
  border-radius: 100%;
  color: #fff;
  text-align: center;
  box-shadow: 0 0 0 3px #fff;
  margin: 0 9px;
}

@media (max-width: 992px) {
  .av-container {
    max-width: 95%;
    margin: 0px auto;
  }
}

@media (max-width: 768px) {
  .av-container {
    height: 100%;
    min-width: 183px;
    padding-bottom: 15px;
  }

  .app-further-details {
    flex-wrap: wrap;
  }

  .circle {
    margin-bottom: 9px;
  }

  input[type="button"] {
    width: 100%;
  }

  .app-cancel {
    text-align: center;
  }
}
</style>
